/** Rangers: generating recruits from the name pools and forming the company (04 §2.1, §4.1). */

import type { Pcg32 } from '../sim/rng';
import type { HazardRules } from './content/career';
import { COMPANY } from './content/economy';
import pools from './content/name-pools.json';
import { RECRUIT_TRAITS } from './content/traits';
import { newRangerId, type ProfileState } from './state';
import type { RangerRecord, TraitId } from './types';

/** A record with nothing on it yet: lowest rank, no perks, fit. */
export function blankRecord(
  id: string, seed: number, firstName: string, surname: string, callsign: string,
): RangerRecord {
  return {
    id, seed, firstName, surname, callsign, rank: 0, merit: 0, perks: [], traits: [], medals: [],
    record: { missions: 0, successes: 0, kills: 0, knifeKills: 0, woundsTaken: 0, timesCaptured: 0 },
    status: 'ready', recoveryMissions: 0, fatigue: 0, veteran: false, pendingPerkChoices: [], traitTimers: {},
  };
}

/** One recruit: a name, a callsign nobody in `takenCallsigns` has, one trait (04 §4.1). */
export function generateRecruit(rng: Pcg32, id: string, takenCallsigns: Set<string>, rules: HazardRules): RangerRecord {
  const seed = rng.nextU32();
  let callsign = rng.pick(pools.callsigns);
  for (let tries = 0; takenCallsigns.has(callsign) && tries < 40; tries++) callsign = rng.pick(pools.callsigns);
  takenCallsigns.add(callsign);
  const recruit = blankRecord(id, seed, rng.pick(pools.firstNames), rng.pick(pools.surnames), callsign);
  const trait: TraitId = rng.pick(RECRUIT_TRAITS);
  recruit.traits.push(trait);
  if (rules.greenRecruits && trait !== 'green') recruit.traits.push('green');
  return recruit;
}

/** How many Hall of Honor veterans may join an operation. */
export function veteranSlots(profile: ProfileState): number {
  return COMPANY.veteranSlots + (profile.unlocks.includes('veteran-slot-2') ? 1 : 0);
}

/**
 * The starting company: the chosen veterans, taken out of the Hall (they are mortal like
 * everyone else, 04 §3), plus generated recruits up to the cap.
 */
export function formCompany(
  profile: ProfileState, veteranIds: string[], rng: Pcg32, rules: HazardRules,
): RangerRecord[] {
  const ids = [...new Set(veteranIds)];
  const slots = veteranSlots(profile);
  if (ids.length > slots) throw new Error(`Only ${slots} veteran slot${slots === 1 ? ' is' : 's are'} unlocked.`);
  const veterans = ids.map((id) => {
    const vet = profile.hall.find((r) => r.id === id);
    if (!vet) throw new Error('That veteran is not in the Hall of Honor.');
    return vet;
  });
  profile.hall = profile.hall.filter((r) => !ids.includes(r.id));
  for (const vet of veterans) {
    vet.veteran = true;
    vet.status = 'ready';
    vet.fatigue = 0;
    vet.recoveryMissions = 0;
  }
  const wanted = COMPANY.recruits + (profile.unlocks.includes('fifth-recruit') ? 1 : 0);
  const recruits = Math.min(wanted, COMPANY.cap - veterans.length);
  const taken = new Set(veterans.map((v) => v.callsign));
  const company = [...veterans];
  for (let i = 0; i < recruits; i++) company.push(generateRecruit(rng, newRangerId(profile), taken, rules));
  return company;
}

/** Validated player-entered name parts; throws a readable Error. */
export function cleanName(text: string, what: string): string {
  const value = text.trim().replace(/\s+/g, ' ');
  if (value.length === 0) throw new Error(`The ${what} cannot be empty.`);
  const max = COMPANY.maxNameLength;
  if (value.length > max) throw new Error(`The ${what} is limited to ${max} characters.`);
  return value;
}
